import React from "react";
import { Box, Chip, Typography, Button, useTheme } from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import PageContainer from "../../components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EditIcon from "@mui/icons-material/Edit";
import { useGetCompanyByIdQuery } from "../../store/apis/companyApi";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { IntegrationType } from "../../types/CompanyTypes";
import { integrationColors } from "../../utils/colorUtils";
import { AudienceManagement } from "../../components/audiences";
import { useOrganizationTypeLabel } from "../../components/company/OrganizationTypesField";
import { LEGAL_NOTE_OPTIONS } from "../../utils/billing/legalNotes";
import { BRAND_PILL_INK } from "../../theme/theme";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import CompanyRegistryPanel from "../../components/company/CompanyRegistryPanel";
import { formatPercentValue, formatFixed } from "../../utils/numberFormat";
import { useGoBack } from "../../hooks/useGoBack";
import CompanyAssignmentsPanel from "../../components/company/CompanyAssignmentsPanel";

const ViewCompany: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const canEditCompany =
    userDb?.role &&
    ["ADMIN", "STRATEGY", "PRICING", "ACCOUNT"].includes(userDb.role);
  // El mismo alcance que los endpoints del registro: el GET lo lee tambien ACCOUNT,
  // pero enlazar y cambiar el sector son PATCH de ADMIN y STRATEGY. Si se le ensena el
  // panel a quien no puede leerlo, lo unico que ve es un 403.
  const canViewRegistry = Boolean(
    userDb?.role && ["ADMIN", "STRATEGY", "ACCOUNT"].includes(userDb.role)
  );
  const canEditRegistry = Boolean(
    userDb?.role && ["ADMIN", "STRATEGY"].includes(userDb.role)
  );
  // Los responsables los lee cualquier usuario interno; el backend no se los da a CLIENT.
  const canViewAssignments = Boolean(userDb?.role && userDb.role !== "CLIENT");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const organizationTypeLabel = useOrganizationTypeLabel();

  const {
    data: companyData,
    isLoading,
    error,
  } = useGetCompanyByIdQuery(id!, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const company = companyData?.data;

  const legalNoteLabel = (legalNote?: string | null) => {
    const option = LEGAL_NOTE_OPTIONS.find(
      (candidate) => candidate.value === (legalNote ?? "")
    );
    return option ? t(option.labelKey) : legalNote;
  };

  // Handle API errors
  React.useEffect(() => {
    if (error) {
      dispatch(
        setToastMessage({
          message: t("company.errors.fetchError"),
          severity: "error",
        })
      );
    }
  }, [error, dispatch, t]);

  const handleEdit = () => {
    navigate(`/companies/${id}/edit`);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                backgroundColor: BRAND_PILL_INK,
                borderRadius: "16px",
                px: 3,
                py: 1.5,
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.secondary50.main,
                }}
              >
                {company?.nameCommercial}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => goBack("/companies")}
                startIcon={<KeyboardBackspaceIcon />}
                sx={{
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[900],
                  "&:hover": {
                    borderColor: theme.palette.grey[300],
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                {t("common.back")}
              </Button>
              {canEditCompany && (
                <Button
                  variant="contained"
                  onClick={handleEdit}
                  startIcon={<EditIcon />}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  {t("common.edit")}
                </Button>
              )}
            </Box>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <OrbLoader
                size="large"
                label={t("common.loading", { defaultValue: "Cargando…" })}
              />
            </Box>
          ) : companyData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {/* Company Logo */}
              {company && company.imageBase64 && (
                <Box
                  sx={{ display: "flex", justifyContent: "center", mb: 0.5 }}
                >
                  <Box
                    component="img"
                    src={`data:image/png;base64,${company.imageBase64}`}
                    alt={company.nameCommercial}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: "cover",
                      borderRadius: 9999,
                      border: `2px solid ${theme.palette.grey[200]}`,
                    }}
                  />
                </Box>
              )}

              {/* Basic Information */}
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "8px",
                  p: 2,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    pb: 1,
                    mb: 1,
                  }}
                >
                  {t("company.basicInfo")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.nameCommercial")}
                    </Typography>
                    <Typography variant="body1">
                      {company?.nameCommercial}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.organizationTypes", "Tipo de organización")}
                    </Typography>
                    {(company?.organizationTypes ?? []).length === 0 ? (
                      <Typography variant="body1" color="text.secondary">
                        {t("company.organizationTypesEmpty", "Sin clasificar")}
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(company?.organizationTypes ?? []).map((type) => (
                          <Chip
                            key={type}
                            size="small"
                            label={organizationTypeLabel(type)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.nameFiscal")}
                    </Typography>
                    <Typography variant="body1">
                      {company?.nameFiscal}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.nif")}
                    </Typography>
                    <Typography variant="body1">{company?.nif}</Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.feeVariable")}
                    </Typography>
                    <Typography variant="body1">
                      {company?.feeVariable != null
                        ? formatPercentValue(Number(company.feeVariable))
                        : "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.feeFixed")}
                    </Typography>
                    <Typography variant="body1">
                      {company?.feeFixed != null
                        ? formatFixed(Number(company.feeFixed), 2)
                        : "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.paymentTerms")}
                    </Typography>
                    <Typography variant="body1">
                      {company?.paymentTerms ||
                        t("company.paymentTermsUndefined")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Contact Information */}
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "8px",
                  p: 2,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    pb: 1,
                    mb: 1,
                  }}
                >
                  {t("company.contactInfo")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.phone")}
                    </Typography>
                    <Typography variant="body1">{company?.phone}</Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Nunito",
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                      }}
                      gutterBottom
                    >
                      {t("company.email")}
                    </Typography>
                    <Typography variant="body1">{company?.email}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Billing Information */}
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "8px",
                  p: 2,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    mb: 1,
                  }}
                >
                  {t("company.billingInfo")}
                </Typography>

                {company?.billings && company.billings.length > 0 ? (
                  // Sort billings so default appears first
                  [...company.billings]
                    .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0))
                    .map((billing) => (
                      <Box
                        key={billing.id}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: billing.isDefault
                            ? `2px solid ${theme.palette.neutral[500]}`
                            : `1px solid ${theme.palette.grey[200]}`,
                          borderRadius: "8px",
                          backgroundColor: billing.isDefault
                            ? theme.palette.neutral[100]
                            : "transparent",
                          position: "relative",
                          boxShadow: billing.isDefault
                            ? "0 2px 4px rgba(0,0,0,0.05)"
                            : "none",
                        }}
                      >
                        {billing.isDefault && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                p: 0.5,
                                px: 1.5,
                                borderRadius: "4px",
                                backgroundColor: BRAND_PILL_INK,
                                color: theme.palette.common.white,
                                fontWeight: 700,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: theme.palette.common.white,
                                  display: "inline-block",
                                  mr: 1,
                                }}
                              />
                              {t("billing.defaultBilling")}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("billing.fiscalName")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.nameFiscal || "—"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.nif")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.nif || "—"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.contactName")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.contactName}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.email")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.email}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.phoneNumber")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.phoneNumber}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.address")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.address}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.city")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.city}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.postalCode")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.postalCode}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.province")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.province}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("company.country")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.country}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("billing.holdedContactId")}
                            </Typography>
                            <Typography variant="body1">
                              {billing.holdedContactId ||
                                t("billing.notLinked")}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                                color: theme.palette.grey[900],
                              }}
                              gutterBottom
                            >
                              {t("billing.legalNote")}
                            </Typography>
                            <Typography variant="body1">
                              {legalNoteLabel(billing.legalNote)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.grey[600] }}
                  >
                    {t("company.noBillingInfo")}
                  </Typography>
                )}
              </Box>

              {/* Integrations - Only visible to admins */}
              {isAdmin && (
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "8px",
                    p: 2,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      pb: 1,
                      mb: 1,
                    }}
                  >
                    {t("company.integrations")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {company?.dv360AdvertiserIds &&
                      company.dv360AdvertiserIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.DV360]
                                .background,
                            color:
                              integrationColors[IntegrationType.DV360].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.DV360}: ${company.dv360AdvertiserIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.taboolaAccountIds &&
                      company.taboolaAccountIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.TABOOLA]
                                .background,
                            color:
                              integrationColors[IntegrationType.TABOOLA].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.TABOOLA}: ${company.taboolaAccountIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.outbrainAccountIds &&
                      company.outbrainAccountIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.OUTBRAIN]
                                .background,
                            color:
                              integrationColors[IntegrationType.OUTBRAIN].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.OUTBRAIN}: ${company.outbrainAccountIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.spotifyAdAccountIds &&
                      company.spotifyAdAccountIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.SPOTIFY]
                                .background,
                            color:
                              integrationColors[IntegrationType.SPOTIFY].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.SPOTIFY}: ${company.spotifyAdAccountIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.hivestackAdvertiserIds &&
                      company.hivestackAdvertiserIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.HIVESTACK]
                                .background,
                            color:
                              integrationColors[IntegrationType.HIVESTACK].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.HIVESTACK}: ${company.hivestackAdvertiserIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.amazonAdvertiserIds &&
                      company.amazonAdvertiserIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.AMAZON]
                                .background,
                            color:
                              integrationColors[IntegrationType.AMAZON].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.AMAZON}: ${company.amazonAdvertiserIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.metaAdAccountIds &&
                      company.metaAdAccountIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.META]
                                .background,
                            color: integrationColors[IntegrationType.META].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.META}: ${company.metaAdAccountIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.tiktokAdvertiserIds &&
                      company.tiktokAdvertiserIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.TIKTOK]
                                .background,
                            color:
                              integrationColors[IntegrationType.TIKTOK].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.TIKTOK}: ${company.tiktokAdvertiserIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.googleAdsCustomerIds &&
                      company.googleAdsCustomerIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.GOOGLE_ADS]
                                .background,
                            color:
                              integrationColors[IntegrationType.GOOGLE_ADS]
                                .text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.GOOGLE_ADS}: ${company.googleAdsCustomerIds.join(", ")}`}
                        </Typography>
                      )}
                    {company?.redditAdAccountIds &&
                      company.redditAdAccountIds.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor:
                              integrationColors[IntegrationType.REDDIT]
                                .background,
                            color:
                              integrationColors[IntegrationType.REDDIT].text,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          {`${IntegrationType.REDDIT}: ${company.redditAdAccountIds.join(", ")}`}
                        </Typography>
                      )}
                  </Box>
                </Box>
              )}
              {/* Assigned users */}
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "8px",
                  p: 2,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Nunito",
                    fontWeight: 700,
                    color: theme.palette.grey[900],
                    pb: 1,
                    mb: 1,
                  }}
                >
                  {t("company.assignedUsers")}
                </Typography>
                {company?.users && company.users.length > 0 ? (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {company.users.map((companyUser) => (
                      <Typography
                        key={companyUser.id}
                        variant="caption"
                        sx={{
                          backgroundColor: theme.palette.grey[100],
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                        }}
                      >
                        {[
                          companyUser.user?.firstName,
                          companyUser.user?.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ") || companyUser.user?.email}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.grey[600] }}
                  >
                    {t("company.noUsersAssigned")}
                  </Typography>
                )}
              </Box>

              {/* Responsables: account / partner success y uno por plataforma. */}
              {id && canViewAssignments && (
                <CompanyAssignmentsPanel
                  companyId={id}
                  canEdit={Boolean(canEditCompany)}
                />
              )}

              {/* Registro comercial: el numero de cliente, el sector y que marca
                  representa esta empresa. Aqui y no solo en el registro porque es
                  desde la ficha desde donde se pregunta "que numero es este cliente".
                  Solo para quien gestiona el registro: el backend acepta el enlace de
                  ADMIN y STRATEGY. */}
              {id && canViewRegistry && (
                <CompanyRegistryPanel
                  companyId={id}
                  canEdit={canEditRegistry}
                />
              )}

              {/* Audiences */}
              {id && <AudienceManagement companyId={id} readOnly={true} />}
            </Box>
          ) : null}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default ViewCompany;
